import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Divider,
} from '@mui/material';
import { CalendarEventDto } from '../../types/calendar';
import { foodService, FoodResponseDto } from '../../services/food.service';
import { NutritionCells } from '../common/NutritionCells';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';

interface FoodEventDetailModalProps {
  open: boolean;
  onClose: () => void;
  event: CalendarEventDto | null;
  onEdit?: (event: CalendarEventDto) => void;
}


interface Nutrition {
  kcal: number | null;
  protein: number | null;
  fat: number | null;
  carbs: number | null;
  fiber: number | null;
}

const round1 = (v: number) => Math.round(v * 10) / 10;

function calcNutritionFromFood(food: FoodResponseDto, qtyG: number): Nutrition {
  const factor = qtyG / 100;
  return {
    kcal:    food.caloriesPer100g != null ? round1(Number(food.caloriesPer100g) * factor) : null,
    protein: food.proteinPer100g  != null ? round1(Number(food.proteinPer100g)  * factor) : null,
    fat:     food.fatPer100g      != null ? round1(Number(food.fatPer100g)       * factor) : null,
    carbs:   food.carbsPer100g    != null ? round1(Number(food.carbsPer100g)     * factor) : null,
    fiber:   food.fiberPer100g    != null ? round1(Number(food.fiberPer100g)     * factor) : null,
  };
}

export const FoodEventDetailModal: React.FC<FoodEventDetailModalProps> = ({ open, onClose, event, onEdit }) => {
  const [food, setFood] = useState<FoodResponseDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !event) {
      setFood(null);
      return;
    }
    // Check associationId first, then fallback to details.foodId
    const foodId = event.associationId || (event.details as any)?.foodId;
    
    if (foodId) {
      setLoading(true);
      foodService.getFoodForEdit(foodId)
        .then(full => setFood(full))
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setFood(null);
    }
  }, [open, event]);

  if (!event) return null;

  const details = event.details as any;
  const measureKey: string | undefined = details?.measureKey;
  const quantity: number | undefined = details?.quantity;
  const qtyG: number | undefined = details?.qtyG;

  const measureLabel = measureKey
    ? (food?.availableMeasures?.find(m => m.key === measureKey)?.label ?? measureKey)
    : undefined;

  // Для продукту з бази — рахуємо з per100g + qtyG
  // Для кастомного — fallback на calc (будь-які поля що є)
  let nutrition: Nutrition | null = null;
  if (food && qtyG != null) {
    nutrition = calcNutritionFromFood(food, qtyG);
  } else if (details?.calc) {
    const c = details.calc;
    // Підтримуємо різні можливі назви полів від бекенду
    nutrition = {
      kcal:    c.cal    ?? c.kcal    ?? null,
      protein: c.pro    ?? c.protein ?? null,
      fat:     c.fat    ?? null,
      carbs:   c.carb   ?? c.carbs   ?? null,
      fiber:   c.fiber  ?? null,
    };
    const hasAny = Object.values(nutrition).some(v => v != null);
    if (!hasAny) nutrition = null;
  }
  // Fallback for custom food without calc object but with raw fields in details (as saved by AddFoodEventModal)
  else if (details && !food) {
     nutrition = {
       kcal: details.calories || details.kcal || null,
       protein: details.protein || null,
       fat: details.fat || null,
       carbs: details.carbs || null,
       fiber: details.fiber || null
     };
     const hasAny = Object.values(nutrition).some(v => v != null);
     if (!hasAny) nutrition = null;
  }

  const scheduledTime = event.scheduledLdt
    ? new Date(event.scheduledLdt).toLocaleString('uk-UA', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth data-i-doc={ModalIndices.FOOD_EVENT_DETAIL}>
      <DialogTitle sx={{ pb: 1 }}>{event.title}</DialogTitle>
      <DialogContent sx={{ pt: 0 }}>

        {loading && !food && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* Зображення */}
        {food?.imageUrl && (
          <Box mb={2}>
            <img
              src={food.imageUrl}
              alt={event.title}
              style={{ width: '100%', maxHeight: 180, borderRadius: 8, objectFit: 'cover', display: 'block' }}
            />
          </Box>
        )}

        {/* Час */}
        {scheduledTime && (
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            {event.isCompleted ? 'Вжито' : 'Заплановано'}: {scheduledTime}
          </Typography>
        )}

        {/* Міра та кількість */}
        {(quantity != null || qtyG != null) && (
          <Box mb={1.5}>
            {quantity != null && measureLabel && (
              <Typography variant="body2">
                Кількість: <strong>{quantity} {measureLabel}</strong>
              </Typography>
            )}
            {qtyG != null && (
              <Typography variant="body2" color="text.secondary">
                Вага: {qtyG} г
              </Typography>
            )}
          </Box>
        )}

        {/* Нутрієнти */}
        {nutrition && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <NutritionCells nutrition={nutrition} />
          </>
        )}

        {/* Рецепт */}
        {food?.details && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
              Рецепт
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
              {food.details}
            </Typography>
          </>
        )}

      </DialogContent>
      <DialogActions>
        {onEdit && (
          <Button onClick={() => { onClose(); onEdit(event); }} color="primary">
            Редагувати
          </Button>
        )}
        <Button onClick={onClose}>Закрити</Button>
      </DialogActions>
    </Dialog>
  );
};
